import React, { useRef, useState } from "react";
import { Paperclip, Trash2, Edit3, Save, X, FileText, File as FileIcon } from "lucide-react";
import { processFile } from "@/utils";
import { SafeLinkText } from "@/components/SafeLinkText";
import { api } from "@/api";
import { toast } from "sonner";

export const NoteCard = ({ note, attachments, onUpdate, onDelete, refreshAttachments, onImagePreview, searchTerm }) => {
    const [editing, setEditing] = useState(false);
    const [title, setTitle] = useState(note.title || "");
    const [text, setText] = useState(note.text);
    const fileRef = useRef(null);
    const atts = (attachments || []).filter((a) => a.parent_id === note.id);

    const save = async () => {
        const patch = {};
        const nextTitle = title.trim();
        if (nextTitle !== (note.title || "")) patch.title = nextTitle || null;
        if (text !== note.text) patch.text = text;
        if (Object.keys(patch).length) await onUpdate(note.id, patch);
        setEditing(false);
    };

    const uploadFiles = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        const loadingId = toast.loading(`Uploading ${files.length} file${files.length === 1 ? "" : "s"}…`);
        let ok = 0;
        let fail = 0;
        for (const f of files) {
            try {
                const p = await processFile(f);
                await api.createAttachment({ parent_id: note.id, name: p.name, type: p.type, data: p.data });
                ok += 1;
                refreshAttachments?.();
            } catch (err) {
                console.error("attachment upload failed:", err);
                fail += 1;
                toast.error(err?.message || `Couldn't save ${f.name}`);
            }
        }
        toast.dismiss(loadingId);
        if (ok > 0) toast.success(`Saved ${ok} attachment${ok === 1 ? "" : "s"}${fail ? ` (${fail} failed)` : ""}`);
        if (fileRef.current) fileRef.current.value = "";
    };

    const deleteAttachment = async (a, e) => {
        e?.stopPropagation();
        try {
            await api.deleteAttachment(a.id);
            toast.success("Attachment removed");
            refreshAttachments?.();
        } catch (err) {
            console.error(err);
            toast.error(err?.message || "Delete failed");
        }
    };

    return (
        <div
            className="bg-meye-card border border-meye-border rounded-md p-4 fade-up group"
            data-testid={`note-card-${note.id}`}
        >
            <input ref={fileRef} type="file" multiple hidden onChange={uploadFiles} />

            {editing ? (
                <div className="space-y-2">
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="title (optional)"
                        className="w-full bg-black/40 border border-meye-border rounded p-2 text-sm font-brand font-bold text-zinc-100 focus:outline-none focus:border-meye-cyan/60"
                        data-testid={`note-edit-title-${note.id}`}
                    />
                    <textarea
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        rows={4}
                        autoFocus
                        className="w-full bg-black/40 border border-meye-border rounded p-2 text-sm text-zinc-100 font-mono focus:outline-none focus:border-meye-cyan/60"
                        data-testid={`note-edit-${note.id}`}
                    />
                </div>
            ) : (
                <div className="space-y-1.5">
                    {note.title && (
                        <h3
                            className="font-brand font-bold text-base text-zinc-100 break-words"
                            data-testid={`note-title-${note.id}`}
                        >
                            <SafeLinkText text={note.title} term={searchTerm} />
                        </h3>
                    )}
                    <p className="text-sm text-zinc-200 whitespace-pre-wrap break-words leading-relaxed">
                        <SafeLinkText text={note.text} term={searchTerm} />
                    </p>
                </div>
            )}

            {atts.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                    {atts.map((a) => {
                        const isImage = a.type === "image";
                        const isPdf =
                            a.type === "pdf" ||
                            (a.data || "").startsWith("data:application/pdf") ||
                            (a.name || "").toLowerCase().endsWith(".pdf");
                        return (
                            <div key={a.id} className="relative">
                                {isImage ? (
                                    <button
                                        type="button"
                                        onClick={() => onImagePreview?.(a)}
                                        className="rounded border border-meye-border hover:border-meye-cyan/60 overflow-hidden transition-colors block"
                                        title={`Preview ${a.name}`}
                                        data-testid={`note-attachment-thumb-${a.id}`}
                                    >
                                        <img
                                            src={a.data}
                                            alt={a.name}
                                            className="w-16 h-16 object-cover cursor-pointer"
                                        />
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => onImagePreview?.(a)}
                                        className="flex items-center gap-1.5 text-xs text-zinc-300 border border-meye-border rounded pl-2 pr-6 py-1.5 hover:border-meye-cyan/60 hover:text-meye-cyan transition-colors max-w-[220px]"
                                        title={`Preview ${a.name}`}
                                        data-testid={`note-attachment-thumb-${a.id}`}
                                    >
                                        {isPdf ? <FileText size={13} /> : <FileIcon size={13} />}
                                        <span className="truncate">{a.name || (isPdf ? "document.pdf" : "attachment")}</span>
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={(e) => deleteAttachment(a, e)}
                                    className={`absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500/90 text-black flex items-center justify-center opacity-80 hover:opacity-100 hover:bg-red-400 shadow-md transition-opacity ${editing ? "" : "hidden"}`}
                                    aria-label={`Delete attachment ${a.name}`}
                                    title="Delete attachment"
                                    data-testid={`note-attachment-delete-${a.id}`}
                                >
                                    <X size={11} strokeWidth={3} />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="flex justify-between mt-3 pt-3 border-t border-meye-border/60 text-xs">
                <div className="flex items-center gap-3">
                    {/* Attach lives only inside edit mode — a read-only card shouldn't tempt a stray
                        tap that opens a native file picker. Tap edit ✎ first, then attach. */}
                    {editing && (
                        <button
                            onClick={() => fileRef.current?.click()}
                            className="text-zinc-500 hover:text-meye-cyan flex items-center gap-1"
                            data-testid={`note-attach-${note.id}`}
                        >
                            <Paperclip size={13} strokeWidth={1.5} /> attach
                        </button>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    {editing ? (
                        <>
                            <button
                                onClick={() => {
                                    setTitle(note.title || "");
                                    setText(note.text);
                                    setEditing(false);
                                }}
                                className="text-zinc-500 hover:text-white"
                            >
                                <X size={13} />
                            </button>
                            <button
                                onClick={save}
                                className="text-meye-cyan hover:text-cyan-300 flex items-center gap-1"
                                data-testid={`note-save-${note.id}`}
                            >
                                <Save size={13} /> save
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                onClick={() => setEditing(true)}
                                className="text-zinc-500 hover:text-meye-cyan"
                                data-testid={`note-edit-btn-${note.id}`}
                            >
                                <Edit3 size={13} />
                            </button>
                            <button
                                onClick={() => onDelete(note.id)}
                                className="text-zinc-500 hover:text-red-400"
                                data-testid={`note-delete-${note.id}`}
                            >
                                <Trash2 size={13} />
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NoteCard;
